import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { FaqEditor } from "~/components/admin/faq-editor";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  approvePlaybookFn,
  archivePlaybookFn,
  createPlaybookDraftFn,
  playbookQueryKeys,
  playbookQueryOptions,
  promotePlaybookDraftFn,
  publishPlaybookFn,
  restorePlaybookFn,
  returnPlaybookToDraftFn,
  updatePlaybookFn,
  type PlaybookRow,
} from "~/lib/functions/cih/playbooks";
import { thoughtLeadersQueryOptions } from "~/lib/functions/cih/thought-leaders";
import { START_HERE_ORDER_SLOTS } from "~/lib/shared/cih/prcpr-taxonomy";
import {
  LIBRARY_SHELF_OPTIONS,
  PRACTICE_STAGE_BAND_OPTIONS,
  PRCPR_LINK_OPTIONS,
  SERIES_FAMILY_OPTIONS,
} from "~/lib/shared/content-status";

/** Sentinel for a Select whose underlying column is nullable. */
const NONE = "__none__";

/** `<Input type="date">` wants `yyyy-mm-dd`; the column is a timestamptz. */
function toDateInputValue(value: Date | null): string {
  return value ? new Date(value).toISOString().slice(0, 10) : "";
}

/** Empty (or non-numeric) input clears the field back to null — never 0/NaN. */
function toNullableInt(raw: string): number | null {
  if (raw.trim() === "") return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

export const Route = createFileRoute("/admin/cih/playbooks/$playbookId")({
  staticData: { title: "Edit Playbook" },
  loader: async ({ context, params }) => {
    const [data] = await Promise.all([
      context.queryClient.ensureQueryData(
        playbookQueryOptions(params.playbookId),
      ),
      context.queryClient.ensureQueryData(thoughtLeadersQueryOptions()),
    ]);
    return { title: data?.title ?? "Edit Playbook" };
  },
  component: PlaybookEditorPage,
});

function PlaybookEditorPage() {
  const { playbookId } = Route.useParams();
  const { data: playbook, isLoading } = useQuery(
    playbookQueryOptions(playbookId),
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Playbook not found.</div>
      </div>
    );
  }

  return <PlaybookEditorForm playbookId={playbookId} playbook={playbook} />;
}

function PlaybookEditorForm({
  playbookId,
  playbook,
}: {
  playbookId: string;
  playbook: PlaybookRow;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState("");

  const { data: thoughtLeaders } = useQuery(thoughtLeadersQueryOptions());

  const form = useForm({
    defaultValues: {
      title: playbook.title,
      thoughtLeaderId: playbook.thoughtLeaderId,
      description: playbook.description,
      body: playbook.body != null ? JSON.stringify(playbook.body) : "",
      faqs: (playbook.faqs ?? []) as { question: string; answer: string }[],
      pullQuote: playbook.pullQuote,
      pullQuoteAttribution: playbook.pullQuoteAttribution,
      reviewerName: playbook.reviewerName,
      reviewerCredentials: playbook.reviewerCredentials,
      reviewedDate: playbook.reviewedDate,
      practiceStageBand: playbook.practiceStageBand,
      ctaStageBand: playbook.ctaStageBand,
      prcprPrimary: playbook.prcprPrimary,
      prcprLinks: playbook.prcprLinks ?? [],
      category: playbook.category,
      shelf: playbook.shelf,
      curriculumOrder: playbook.curriculumOrder,
      seriesFamily: playbook.seriesFamily,
      seriesPosition: playbook.seriesPosition,
      lastTaxonomyReview: toDateInputValue(playbook.lastTaxonomyReview),
      startHereOrder: playbook.startHereOrder,
    },
  });

  const showSuccess = (msg: string): void => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: playbookQueryKeys.all });
    queryClient.invalidateQueries({
      queryKey: playbookQueryKeys.detail(playbookId),
    });
  };

  const saveAll = async () => {
    const v = form.state.values;
    await updatePlaybookFn({
      data: {
        id: playbookId,
        title: v.title,
        thoughtLeaderId: v.thoughtLeaderId,
        description: v.description,
        body: v.body,
        faqs: v.faqs,
        pullQuote: v.pullQuote,
        pullQuoteAttribution: v.pullQuoteAttribution,
        reviewerName: v.reviewerName,
        reviewerCredentials: v.reviewerCredentials,
        reviewedDate: v.reviewedDate,
        practiceStageBand: v.practiceStageBand,
        ctaStageBand: v.ctaStageBand,
        prcprPrimary: v.prcprPrimary,
        prcprLinks: v.prcprLinks.length > 0 ? v.prcprLinks : null,
        category: v.category,
        shelf: v.shelf,
        curriculumOrder: v.curriculumOrder,
        seriesFamily: v.seriesFamily,
        seriesPosition: v.seriesPosition,
        lastTaxonomyReview: v.lastTaxonomyReview
          ? new Date(v.lastTaxonomyReview)
          : null,
        startHereOrder: v.startHereOrder,
      },
    });
  };

  const updateMutation = useMutation({
    mutationFn: saveAll,
    onSuccess: () => {
      invalidate();
      showSuccess("Playbook saved.");
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (data: { data: { id: string } }) => {
      await saveAll();
      return publishPlaybookFn(data);
    },
    onSuccess: () => {
      invalidate();
      showSuccess("Playbook published.");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: archivePlaybookFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Playbook archived.");
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: createPlaybookDraftFn,
    onSuccess: (newDraft) => {
      invalidate();
      navigate({
        to: "/admin/cih/playbooks/$playbookId",
        params: { playbookId: newDraft.id },
      });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (data: { data: { draftId: string } }) => {
      await saveAll();
      return promotePlaybookDraftFn(data);
    },
    onSuccess: (promoted) => {
      queryClient.removeQueries({
        queryKey: playbookQueryKeys.detail(playbookId),
      });
      invalidate();
      navigate({
        to: "/admin/cih/playbooks/$playbookId",
        params: { playbookId: promoted.id },
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: approvePlaybookFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Playbook approved and published.");
    },
  });

  const returnToDraftMutation = useMutation({
    mutationFn: returnPlaybookToDraftFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Playbook returned to draft.");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: restorePlaybookFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Playbook restored to draft.");
    },
  });

  const handleSave = (): void => {
    updateMutation.mutate();
  };

  const handleReturnToDraft = (): void => {
    const note = window.prompt("Feedback note for the author:");
    if (!note) return;
    returnToDraftMutation.mutate({ data: { id: playbookId, note } });
  };

  const anyPending =
    updateMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending ||
    createDraftMutation.isPending ||
    promoteMutation.isPending ||
    approveMutation.isPending ||
    returnToDraftMutation.isPending ||
    restoreMutation.isPending;

  const mutationError =
    updateMutation.error ??
    publishMutation.error ??
    archiveMutation.error ??
    createDraftMutation.error ??
    promoteMutation.error ??
    approveMutation.error ??
    returnToDraftMutation.error ??
    restoreMutation.error;

  const isDraft = playbook.status === "draft";
  const isPublished = playbook.status === "published";
  const isInReview = playbook.status === "in_review";
  const isArchived = playbook.status === "archived";
  const hasDraftParent = playbook.draftOf != null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-medium text-gray-900">
                {playbook.title}
              </h1>
              <ContentStatusBadge status={playbook.status} />
            </div>
            <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
              <span>Playbook editor — ID: {playbookId}</span>
              {playbook.slug && playbook.status === "published" && (
                <a
                  href={`/coaching-business/playbooks/${playbook.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                >
                  View public page
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {isDraft && (
              <form.Subscribe
                selector={(state) => state.values.thoughtLeaderId}
              >
                {(thoughtLeaderId) =>
                  hasDraftParent ? (
                    <Button
                      variant="default"
                      onClick={handleSave}
                      disabled={anyPending || !thoughtLeaderId}
                      loading={updateMutation.isPending}
                    >
                      Save
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="default"
                        onClick={handleSave}
                        disabled={anyPending || !thoughtLeaderId}
                        loading={updateMutation.isPending}
                      >
                        Save
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() =>
                          publishMutation.mutate({ data: { id: playbookId } })
                        }
                        disabled={anyPending || !thoughtLeaderId}
                        loading={publishMutation.isPending}
                      >
                        Publish
                      </Button>
                    </>
                  )
                }
              </form.Subscribe>
            )}
            {isPublished && (
              <>
                <Button
                  variant="default"
                  onClick={() =>
                    createDraftMutation.mutate({
                      data: { publishedId: playbookId },
                    })
                  }
                  disabled={anyPending}
                  loading={createDraftMutation.isPending}
                >
                  Create Draft
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    archiveMutation.mutate({ data: { id: playbookId } })
                  }
                  disabled={anyPending}
                  loading={archiveMutation.isPending}
                >
                  Archive
                </Button>
              </>
            )}
            {isInReview && (
              <>
                <Button
                  variant="primary"
                  onClick={() =>
                    approveMutation.mutate({ data: { id: playbookId } })
                  }
                  disabled={anyPending}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="default"
                  onClick={handleReturnToDraft}
                  disabled={anyPending}
                  loading={returnToDraftMutation.isPending}
                >
                  Return to Draft
                </Button>
              </>
            )}
            {isArchived && (
              <Button
                variant="default"
                onClick={() =>
                  restoreMutation.mutate({ data: { id: playbookId } })
                }
                disabled={anyPending}
                loading={restoreMutation.isPending}
              >
                Restore to Draft
              </Button>
            )}
          </div>
        </div>

        {/* Draft-of banner */}
        {hasDraftParent && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-700">
              Editing draft of published content. Save your changes, then
              promote when ready.
            </p>
            <Button
              size="sm"
              onClick={() =>
                promoteMutation.mutate({ data: { draftId: playbookId } })
              }
              disabled={anyPending}
              loading={promoteMutation.isPending}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              Promote to Live
            </Button>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {mutationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {mutationError instanceof Error
              ? mutationError.message
              : "An error occurred. Please try again."}
          </div>
        )}

        {/* Form */}
        <Card className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="title">
              {(field) => (
                <div>
                  <Label htmlFor="playbook-title" className="mb-1">
                    Title
                  </Label>
                  <Input
                    id="playbook-title"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </div>
              )}
            </form.Field>
            <div>
              <Label htmlFor="playbook-slug" className="mb-1">
                Slug
              </Label>
              <Input
                id="playbook-slug"
                type="text"
                value={playbook.slug ?? ""}
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>

          <div>
            <form.Field name="thoughtLeaderId">
              {(field) => (
                <div>
                  <Label htmlFor="playbook-thought-leader" className="mb-1">
                    Thought leader
                  </Label>
                  <Select
                    value={field.state.value || undefined}
                    onValueChange={field.handleChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a thought leader" />
                    </SelectTrigger>
                    <SelectContent>
                      {(thoughtLeaders ?? []).map((tl) => (
                        <SelectItem key={tl.id} value={tl.id}>
                          {tl.name}
                          {tl.organization ? ` — ${tl.organization}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </div>

          <form.Field name="description">
            {(field) => (
              <div>
                <Label htmlFor="playbook-description" className="mb-1">
                  Description
                </Label>
                <Textarea
                  id="playbook-description"
                  rows={3}
                  value={field.state.value}
                  onValueChange={field.handleChange}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="body">
            {(field) => (
              <div>
                <Label className="mb-1">Body</Label>
                <RichTextEditor
                  content={field.state.value}
                  onChange={field.handleChange}
                />
              </div>
            )}
          </form.Field>
        </Card>

        {/* Pull quote */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Pull Quote
          </h2>
          <form.Field name="pullQuote">
            {(field) => (
              <div>
                <Label htmlFor="playbook-pull-quote" className="mb-1">
                  Pull quote
                </Label>
                <Textarea
                  id="playbook-pull-quote"
                  rows={3}
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="One sentence that captures the playbook's essence"
                />
              </div>
            )}
          </form.Field>
          <form.Field name="pullQuoteAttribution">
            {(field) => (
              <div className="mt-4">
                <Label htmlFor="playbook-pull-quote-attr" className="mb-1">
                  Pull quote attribution
                </Label>
                <Input
                  id="playbook-pull-quote-attr"
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="e.g., Alex Hormozi"
                />
              </div>
            )}
          </form.Field>
        </Card>

        {/* Reviewer byline */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Reviewer Byline
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="reviewerName">
              {(field) => (
                <div>
                  <Label htmlFor="playbook-reviewer-name" className="mb-1">
                    Reviewer name
                  </Label>
                  <Input
                    id="playbook-reviewer-name"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., Dr. Jane Smith"
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="reviewerCredentials">
              {(field) => (
                <div>
                  <Label
                    htmlFor="playbook-reviewer-credentials"
                    className="mb-1"
                  >
                    Reviewer credentials
                  </Label>
                  <Input
                    id="playbook-reviewer-credentials"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., PCC, MBA"
                  />
                </div>
              )}
            </form.Field>
          </div>
          <form.Field name="reviewedDate">
            {(field) => (
              <div className="mt-4">
                <Label htmlFor="playbook-reviewed-date" className="mb-1">
                  Reviewed date
                </Label>
                <Input
                  id="playbook-reviewed-date"
                  type="text"
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="e.g., June 2026"
                />
              </div>
            )}
          </form.Field>
        </Card>

        {/* PrcPR taxonomy */}
        <Card className="mt-6">
          <h2 className="mb-1 text-sm font-medium tracking-wide text-gray-500 uppercase">
            PrcPR Taxonomy
          </h2>
          <p className="mb-4 text-sm text-gray-500">
            Where this playbook sits on the coach acquisition chain, and which
            revenue band it is written for.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="practiceStageBand">
              {(field) => (
                <div>
                  <Label
                    htmlFor="playbook-practice-stage-band"
                    className="mb-1"
                  >
                    Practice stage band
                  </Label>
                  <Select
                    value={field.state.value ?? NONE}
                    onValueChange={(val) =>
                      field.handleChange(
                        PRACTICE_STAGE_BAND_OPTIONS.find(
                          (band) => band.value === val,
                        )?.value ?? null,
                      )
                    }
                  >
                    <SelectTrigger
                      id="playbook-practice-stage-band"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not set</SelectItem>
                      {PRACTICE_STAGE_BAND_OPTIONS.map((band) => (
                        <SelectItem key={band.value} value={band.value}>
                          {band.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
            <form.Field name="ctaStageBand">
              {(field) => (
                <div>
                  <Label htmlFor="playbook-cta-stage-band" className="mb-1">
                    CTA stage band
                  </Label>
                  <Select
                    value={field.state.value ?? NONE}
                    onValueChange={(val) =>
                      field.handleChange(
                        PRACTICE_STAGE_BAND_OPTIONS.find(
                          (band) => band.value === val,
                        )?.value ?? null,
                      )
                    }
                  >
                    <SelectTrigger
                      id="playbook-cta-stage-band"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not set</SelectItem>
                      {PRACTICE_STAGE_BAND_OPTIONS.map((band) => (
                        <SelectItem key={band.value} value={band.value}>
                          {band.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-gray-400">
                    Picks the Coach Business Builder box at the foot of the
                    page. Not set or All shows the 6-Figure Readiness Score.
                  </p>
                </div>
              )}
            </form.Field>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <form.Field name="prcprPrimary">
              {(field) => (
                <div>
                  <Label htmlFor="playbook-prcpr-primary" className="mb-1">
                    Primary chain link
                  </Label>
                  <Select
                    value={field.state.value ?? NONE}
                    onValueChange={(val) =>
                      field.handleChange(
                        PRCPR_LINK_OPTIONS.find((link) => link.value === val)
                          ?.value ?? null,
                      )
                    }
                  >
                    <SelectTrigger
                      id="playbook-prcpr-primary"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not set</SelectItem>
                      {PRCPR_LINK_OPTIONS.map((link) => (
                        <SelectItem key={link.value} value={link.value}>
                          {link.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
            <form.Field name="category">
              {(field) => (
                <div>
                  <Label htmlFor="playbook-category" className="mb-1">
                    Category
                  </Label>
                  <Input
                    id="playbook-category"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., Marketing"
                  />
                </div>
              )}
            </form.Field>
          </div>

          <form.Field name="prcprLinks">
            {(field) => (
              <div className="mt-4">
                <Label className="mb-2">All chain links touched</Label>
                <div className="grid grid-cols-3 gap-2">
                  {PRCPR_LINK_OPTIONS.map((link) => (
                    <Checkbox
                      key={link.value}
                      id={`playbook-prcpr-link-${link.value}`}
                      checked={field.state.value.includes(link.value)}
                      onCheckedChange={(checked) =>
                        field.handleChange(
                          checked === true
                            ? [...field.state.value, link.value]
                            : field.state.value.filter((v) => v !== link.value),
                        )
                      }
                    >
                      {link.label}
                    </Checkbox>
                  ))}
                </div>
              </div>
            )}
          </form.Field>

          <div className="mt-6 border-t border-gray-100 pt-6">
            <h3 className="mb-1 text-sm font-medium text-gray-700">
              Library placement
            </h3>
            <p className="mb-4 text-sm text-gray-500">
              Shelf, curriculum order, and series are normally owned by the
              Knowledge Layer Mapping sheet and backfilled from it — a manual
              edit here will be overwritten by the next backfill run unless the
              sheet is updated too.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <form.Field name="shelf">
                {(field) => (
                  <div>
                    <Label htmlFor="playbook-shelf" className="mb-1">
                      Shelf
                    </Label>
                    <Select
                      value={field.state.value ?? NONE}
                      onValueChange={(val) =>
                        field.handleChange(
                          LIBRARY_SHELF_OPTIONS.find((s) => s.value === val)
                            ?.value ?? null,
                        )
                      }
                    >
                      <SelectTrigger id="playbook-shelf" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Not set</SelectItem>
                        {LIBRARY_SHELF_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
              <form.Field name="curriculumOrder">
                {(field) => (
                  <div>
                    <Label htmlFor="playbook-curriculum-order" className="mb-1">
                      Curriculum order{" "}
                      <span className="font-normal text-gray-400">
                        (within shelf)
                      </span>
                    </Label>
                    <Input
                      id="playbook-curriculum-order"
                      type="number"
                      min={1}
                      step={1}
                      value={field.state.value ?? ""}
                      onChange={(e) =>
                        field.handleChange(toNullableInt(e.target.value))
                      }
                      placeholder="e.g., 3"
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <form.Field name="seriesFamily">
                {(field) => (
                  <div>
                    <Label htmlFor="playbook-series-family" className="mb-1">
                      Series
                    </Label>
                    <Select
                      value={field.state.value ?? NONE}
                      onValueChange={(val) =>
                        field.handleChange(
                          SERIES_FAMILY_OPTIONS.find((s) => s.value === val)
                            ?.value ?? null,
                        )
                      }
                    >
                      <SelectTrigger
                        id="playbook-series-family"
                        className="w-full"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Not set</SelectItem>
                        {SERIES_FAMILY_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
              <form.Field name="seriesPosition">
                {(field) => (
                  <div>
                    <Label htmlFor="playbook-series-position" className="mb-1">
                      Series position
                    </Label>
                    <Input
                      id="playbook-series-position"
                      type="number"
                      min={1}
                      step={1}
                      value={field.state.value ?? ""}
                      onChange={(e) =>
                        field.handleChange(toNullableInt(e.target.value))
                      }
                      placeholder="e.g., 2"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Only meaningful when a series is selected above.
                    </p>
                  </div>
                )}
              </form.Field>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <form.Field name="startHereOrder">
              {(field) => (
                <div>
                  <Label htmlFor="playbook-start-here-order" className="mb-1">
                    Start Here slot
                  </Label>
                  <Select
                    value={
                      field.state.value === null
                        ? NONE
                        : String(field.state.value)
                    }
                    onValueChange={(val) =>
                      field.handleChange(
                        START_HERE_ORDER_SLOTS.find(
                          (slot) => String(slot) === val,
                        ) ?? null,
                      )
                    }
                  >
                    <SelectTrigger
                      id="playbook-start-here-order"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not on the shelf</SelectItem>
                      {START_HERE_ORDER_SLOTS.map((slot) => (
                        <SelectItem key={slot} value={String(slot)}>
                          Slot {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
            <form.Field name="lastTaxonomyReview">
              {(field) => (
                <div>
                  <Label
                    htmlFor="playbook-last-taxonomy-review"
                    className="mb-1"
                  >
                    Last taxonomy review
                  </Label>
                  <Input
                    id="playbook-last-taxonomy-review"
                    type="date"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>
          </div>
        </Card>

        {/* FAQs */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            FAQs
          </h2>
          <form.Field name="faqs">
            {(field) => (
              <FaqEditor
                value={field.state.value}
                onChange={field.handleChange}
              />
            )}
          </form.Field>
        </Card>

        {/* Sticky save for draft — gated on thought-leader selection */}
        {isDraft && (
          <form.Subscribe selector={(state) => state.values.thoughtLeaderId}>
            {(thoughtLeaderId) => (
              <div className="mt-4 flex items-center justify-end gap-3">
                {!thoughtLeaderId && (
                  <span className="text-sm text-red-600">
                    Select a thought leader before saving.
                  </span>
                )}
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={anyPending || !thoughtLeaderId}
                  loading={updateMutation.isPending}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </form.Subscribe>
        )}
      </div>
    </div>
  );
}
